/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';

import {
	updateCommentHighlightsInEditor,
	getEditorResolvedState,
	getSortedMarkerRefs,
} from '@confluence/comments-util';

import {
	CommentType,
	GraphQLContentStatus,
	Depth,
} from './__types__/useCommentRefetchInlineCommentTopLevelMarkerRefsQuery';
import type {
	useCommentRefetchInlineCommentTopLevelMarkerRefsQuery as useCommentRefetchInlineCommentTopLevelMarkerRefsQuery$data,
	useCommentRefetchInlineCommentTopLevelMarkerRefsQueryVariables as useCommentRefetchInlineCommentTopLevelMarkerRefsQuery$variables,
} from './__types__/useCommentRefetchInlineCommentTopLevelMarkerRefsQuery';

const useCommentRefetchInlineCommentTopLevelMarkerRefsQuery = gql`
	query useCommentRefetchInlineCommentTopLevelMarkerRefsQuery(
		$pageId: ID!
		$contentStatus: [GraphQLContentStatus]
		$depth: Depth
		$type: [CommentType]
	) {
		comments(pageId: $pageId, contentStatus: $contentStatus, depth: $depth, type: $type) {
			nodes {
				id
				parentId
				location {
					type
					... on InlineComment {
						inlineMarkerRef
					}
				}
			}
		}
	}
`;
type CommentRefetchProps = {
	isInitialRender: boolean;
	setIsInitialRender: React.Dispatch<React.SetStateAction<boolean>>;
	isDocumentActive: boolean;
	contentId: string;
	triggerAnalyticsEvent: (
		eventName: string,
		extraData?: {
			[key: string]: any;
		},
	) => Promise<void>;
	updateUnresolvedInlineComment: (unresolvedInlineComments: string[]) => void;
	handleGqlError: (error: Error) => void;
	previousAnnotations: string[];
	updateUnreadCommentsOnFirstRender: (shouldUpdateUnreadCommentsOnFirstRender: boolean) => void;
	updateUnreadComments: (shouldUpdateUnreadComments: boolean) => void;
};

export const useCommentRefetch = ({
	isInitialRender,
	setIsInitialRender,
	isDocumentActive,
	contentId,
	triggerAnalyticsEvent,
	updateUnresolvedInlineComment,
	handleGqlError,
	previousAnnotations,
	updateUnreadCommentsOnFirstRender,
	updateUnreadComments,
}: CommentRefetchProps) => {
	const { refetch } = useQuery<
		useCommentRefetchInlineCommentTopLevelMarkerRefsQuery$data,
		useCommentRefetchInlineCommentTopLevelMarkerRefsQuery$variables
	>(useCommentRefetchInlineCommentTopLevelMarkerRefsQuery, {
		variables: {
			pageId: contentId,
			contentStatus: [GraphQLContentStatus.DRAFT, GraphQLContentStatus.CURRENT],
		},
		skip: true,
		onError: handleGqlError,
		fetchPolicy: 'network-only',
	});
	useEffect(() => {
		if (isInitialRender) {
			setIsInitialRender(false);
		} else if (isDocumentActive) {
			refetch({
				pageId: contentId,
				contentStatus: [GraphQLContentStatus.DRAFT, GraphQLContentStatus.CURRENT],
				depth: Depth.ROOT,
				type: [CommentType.UNRESOLVED],
			})
				.then((response) => {
					const updatedAnnotations = updateCommentHighlightsInEditor({
						data: response.data,
						previousAnnotations,
					});

					// organizes the annotations for navigation to work
					if (updatedAnnotations.length) {
						void getEditorResolvedState({
							annotations: getSortedMarkerRefs(updatedAnnotations, 'edit'),
							pageId: contentId,
							triggerAnalyticsEvent,
							updateUnresolvedInlineComment,
						});

						updateUnreadCommentsOnFirstRender(true);
						updateUnreadComments(true);
					}
				})
				.catch((error) => {
					handleGqlError(error);
				});
		}
		// we only want to update the comment highlights when the user comes back to the page
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isInitialRender, isDocumentActive]);
};
